'use client'

import { useState } from 'react'

const inputStyle = {
  flex: '1 1 180px',
  padding: '8px 12px',
  borderRadius: 6,
  border: '1px solid var(--theme-elevation-150)',
  background: 'var(--theme-input-bg, var(--theme-elevation-0))',
  color: 'var(--theme-text)',
}

// Renders above the Z2H Applications list. Fast-track manual entry: brings a
// founder into the program WITHOUT the AI interview — creates an approved
// application (source: manual) stitched to the CRM, ready for the deal desk.
export function ManualEntryPanel() {
  const [founderName, setFounderName] = useState('')
  const [email, setEmail] = useState('')
  const [ventureName, setVentureName] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [brief, setBrief] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)

  async function create(e) {
    e.preventDefault()
    if (!email.trim() || !founderName.trim() || busy) return
    setBusy(true)
    setResult(null)
    try {
      const res = await fetch('/api/z2h/manual-entry', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          founderName: founderName.trim(),
          email: email.trim(),
          ventureName: ventureName.trim() || undefined,
          companyName: companyName.trim() || undefined,
          brief: brief.trim() || undefined,
        }),
      })
      const data = await res.json()
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`)
      setResult({
        ok: true,
        message: `Application #${data.applicationId} created (approved, fast-track) and stitched to the CRM. Generate the offer from the Agreements panel using this application ID — the AI will suggest terms from your brief.`,
      })
      setFounderName('')
      setEmail('')
      setVentureName('')
      setCompanyName('')
      setBrief('')
    } catch (err) {
      setResult({ ok: false, message: err.message })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      style={{
        marginBottom: 20,
        padding: 16,
        borderRadius: 8,
        border: '1px solid var(--theme-elevation-150)',
        background: 'var(--theme-elevation-50)',
      }}
    >
      <h3 style={{ margin: 0, fontSize: '1rem' }}>Manual entry (fast-track)</h3>
      <p style={{ margin: '6px 0 12px', fontSize: '0.8rem', color: 'var(--theme-elevation-600)' }}>
        Skip the AI interview: bring a founder straight into the program from a
        contact or company. Creates an approved application linked to the CRM;
        the offer, signing, and project flow work exactly as usual.
      </p>
      <form onSubmit={create} style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        <input
          type="text"
          placeholder="Founder name *"
          value={founderName}
          onChange={(e) => setFounderName(e.target.value)}
          style={inputStyle}
        />
        <input
          type="email"
          placeholder="founder@company.com *"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={inputStyle}
        />
        <input
          type="text"
          placeholder="Venture name"
          value={ventureName}
          onChange={(e) => setVentureName(e.target.value)}
          style={inputStyle}
        />
        <input
          type="text"
          placeholder="Company (CRM)"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          style={inputStyle}
        />
        <textarea
          placeholder="Venture brief — traction, scope, financial readiness. The AI suggests offer terms from this."
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          rows={3}
          style={{ ...inputStyle, flex: '1 1 100%', resize: 'vertical' }}
        />
        <button
          type="submit"
          disabled={busy || !email.trim() || !founderName.trim()}
          style={{
            padding: '8px 18px',
            borderRadius: 6,
            border: 'none',
            cursor: 'pointer',
            background: 'var(--theme-elevation-800)',
            color: 'var(--theme-elevation-0)',
            opacity: busy || !email.trim() || !founderName.trim() ? 0.6 : 1,
          }}
        >
          {busy ? 'Creating…' : 'Create fast-track application'}
        </button>
      </form>
      {result && (
        <p
          style={{
            marginTop: 10,
            fontSize: '0.85rem',
            color: result.ok ? 'var(--theme-success-500)' : 'var(--theme-error-500)',
          }}
        >
          {result.message}
        </p>
      )}
    </div>
  )
}
